<template>
  <div :class="['relative flex h-full flex-col bg-bg', isMapExpanded && isDesktop ? 'overflow-hidden' : '']">
    <ExploreFilterBar
      v-model="filters"
      :result-count="propertiesTotal"
      :is-loading="isLoadingAny"
      @apply="applyFilters()"
      @reset="resetFilters"
      @select-suggestion="handleSmartSearchSuggestion"
    />

    <div
      :class="[
        'mx-auto flex w-full max-w-[1680px] flex-1 flex-col gap-0 px-0 desktop:flex-row',
        isMapExpanded && isDesktop ? 'desktop:overflow-hidden desktop:h-0' : '',
      ]"
    >
      <!-- Left column: rich list -->
      <div
        v-if="!isMapOpen || (isDesktop && !isMapExpanded)"
        ref="propertiesContainer"
        :class="[
          'base-scrollbar relative flex w-full flex-col gap-4 px-4 pb-24 pt-4 tablet:px-8 desktop:flex-1 desktop:px-5 desktop:pb-12 desktop:pt-6',
          isMapOpen && isDesktop && !isMapExpanded ? 'desktop:max-h-[calc(100dvh-220px)] desktop:overflow-y-auto' : '',
        ]"
        @scroll="checkScroll"
      >
        <!-- List head — moved here from the Hero (matches .pr-ml-list-head). -->
        <div class="flex flex-wrap items-baseline justify-between gap-2">
          <div class="flex items-baseline gap-2">
            <span class="font-serif text-[22px] font-semibold text-black-100">
              {{ propertiesTotal }}
            </span>
            <span class="text-[14px] text-black-60">
              {{ t('explore.page.list.totalProjects', { count: propertiesTotal }, propertiesTotal) }}
            </span>
          </div>
          <div class="flex items-center gap-3">
            <span class="font-mono text-[10px] uppercase tracking-[0.1em] text-black-60">
              {{ t('explore.page.list.lastUpdated') }} · {{ t('explore.page.list.today') }}
            </span>
            <!-- Clear the map-selected card (first card in the grid). -->
            <button
              v-if="selectedCard"
              type="button"
              class="inline-flex h-8 items-center gap-1.5 rounded-[8px] border border-gold-primary-100 bg-gold-primary-10/40 px-2.5 text-[12px] font-semibold text-gold-primary-120 transition-colors hover:bg-gold-primary-10"
              @click="clearMarkerSelection"
            >
              <svg
                width="12"
                height="12"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
                aria-hidden="true"
              >
                <path d="M18 6 6 18M6 6l12 12" />
              </svg>
              <span>{{ t('explore.page.list.clearSelection') }}</span>
            </button>
            <!-- Desktop-only: collapse/expand the map column to give the list
                 the full width. -->
            <button
              v-if="isDesktop"
              type="button"
              class="inline-flex h-8 items-center gap-1.5 rounded-[8px] border border-black/[0.08] bg-white px-2.5 text-[12px] font-semibold text-black-100 transition-colors hover:bg-black-03"
              :aria-pressed="isMapCollapsed"
              @click="isMapCollapsed = !isMapCollapsed"
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
                aria-hidden="true"
              >
                <path d="M9 3v18" />
                <rect
                  x="3"
                  y="3"
                  width="18"
                  height="18"
                  rx="2"
                />
              </svg>
              <span>{{ isMapCollapsed ? t('explore.page.list.showMap') : t('explore.page.list.hideMap') }}</span>
            </button>
            <div
              ref="sortDropdownRef"
              class="relative"
            >
              <button
                type="button"
                class="inline-flex h-8 items-center gap-1.5 rounded-[8px] border border-black/[0.08] bg-white px-2.5 text-[12px] font-semibold text-black-100 transition-colors hover:bg-black-03"
                @click="isSortOpen = !isSortOpen"
              >
                <span class="font-mono text-[10px] uppercase tracking-[0.1em] text-black-60">
                  {{ t('explore.page.list.sortLabel') }}
                </span>
                <i18n-t
                  :keypath="currentSortKey"
                  scope="global"
                  tag="span"
                >
                  <template #arrow>
                    <span
                      aria-hidden="true"
                      class="rtl:inline-block rtl:-scale-x-100"
                      >→</span
                    >
                  </template>
                </i18n-t>
                <svg
                  width="10"
                  height="10"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  aria-hidden="true"
                >
                  <polyline points="6 9 12 15 18 9" />
                </svg>
              </button>
              <div
                v-if="isSortOpen"
                class="absolute end-0 top-[calc(100%+6px)] z-40 w-56 rounded-[10px] border border-black/[0.08] bg-white p-1 shadow-[0_8px_24px_rgba(13,15,21,0.06)]"
              >
                <button
                  v-for="option in sortOptions"
                  :key="option.value"
                  type="button"
                  class="flex w-full items-center justify-between gap-2 rounded-md px-3 py-2 text-start text-[13px] text-black-100 hover:bg-black/[0.03]"
                  :class="{ 'bg-black/[0.03] font-semibold': option.value === currentSortValue }"
                  @click="pickSort(option.value)"
                >
                  <i18n-t
                    :keypath="option.key"
                    scope="global"
                    tag="span"
                  >
                    <template #arrow>
                      <span
                        aria-hidden="true"
                        class="rtl:inline-block rtl:-scale-x-100"
                        >→</span
                      >
                    </template>
                  </i18n-t>
                  <UIIcon
                    v-if="option.value === currentSortValue"
                    name="check"
                    class="!size-4 text-gold-primary-120"
                  />
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- List: 2-col when map is sticky next to it (matches .pr-ml-grid),
             3-col when the map is hidden so the list reclaims the width. -->

        <!-- 100% Commission chip on but the viewer can't see exclusives (ID-118) -->
        <div
          v-if="exclusiveGate"
          class="rounded-2xl border border-black-10 bg-white px-6 py-14 text-center"
        >
          <div class="mx-auto flex max-w-[440px] flex-col items-center gap-4">
            <h2 class="font-serif text-[20px] font-semibold text-black-100">
              {{
                exclusiveGate === 'emirates_id'
                  ? t('exclusives.explore.emiratesIdTitle')
                  : t('exclusives.explore.signInTitle')
              }}
            </h2>
            <NuxtLink
              v-if="exclusiveGate === 'emirates_id'"
              :to="UAE_VERIFY_ID_ROUTE"
              class="inline-flex h-10 items-center justify-center rounded-xl bg-gold-primary-100 px-5 text-sm font-semibold text-black-100 transition-colors hover:bg-gold-primary-120"
            >
              {{ t('exclusives.explore.emiratesIdCta') }}
            </NuxtLink>
            <NuxtLink
              v-else
              :to="exclusiveSignInHref"
              class="inline-flex h-10 items-center justify-center rounded-xl bg-gold-primary-100 px-5 text-sm font-semibold text-black-100 transition-colors hover:bg-gold-primary-120"
            >
              {{ t('exclusives.explore.signInCta') }}
            </NuxtLink>
          </div>
        </div>

        <div
          v-else-if="formattedProperties.length || selectedCard"
          ref="cardList"
          :class="['grid gap-[18px]', cardGridColsClass]"
        >
          <!-- Map-selected project pinned as the FIRST card (highlighted),
               pushing the rest down. De-duped from the list below. -->
          <PropertiesProjectCard
            v-if="selectedCard"
            :key="'selected-' + selectedCard.complex_id"
            :property="selectedCard"
            :is-favorite="isFavorite(selectedCard.complex_id)"
            is-highlighted
            open-source="map"
            :hero-override="heroFor(selectedCard)?.url ?? null"
            :hero-portrait="heroFor(selectedCard)?.portrait ?? false"
            @toggle-favorite="toggleFavorite(selectedCard.complex_id)"
            @hover="onCardHover"
          />
          <PropertiesProjectCard
            v-for="property in listPropertiesWithoutSelected"
            :key="property.complex_id"
            :property="property"
            :is-favorite="isFavorite(property.complex_id)"
            :is-loading="isCardLoading(property.complex_id)"
            :open-source="filters.exclusive ? 'filtered_list' : 'list'"
            :hero-override="heroFor(property)?.url ?? null"
            :hero-portrait="heroFor(property)?.portrait ?? false"
            @toggle-favorite="toggleFavorite(property.complex_id)"
            @hover="onCardHover"
          />
        </div>

        <!-- Empty state -->
        <div
          v-else-if="!isLoadingAny"
          class="flex flex-col items-center gap-3"
        >
          <PortalUiEmptyState
            v-if="filters.exclusive"
            :title="t('exclusives.explore.emptyTitle')"
            icon-name="search"
          />
          <PortalUiEmptyState
            v-else
            :title="t('explore.page.emptyState.title')"
            :description="t('explore.page.emptyState.description')"
            icon-name="search"
          />
          <button
            type="button"
            class="-mt-3 inline-flex h-10 items-center justify-center gap-1 rounded-xl bg-gold-primary-100 px-5 text-sm font-semibold text-black-100 transition-colors hover:bg-gold-primary-120"
            @click="filters.exclusive ? clearOtherFilters() : resetFilters()"
          >
            {{
              filters.exclusive
                ? t('exclusives.explore.clearOtherFilters')
                : t('explore.page.emptyState.clearAllFilters')
            }}
          </button>
        </div>

        <!-- Initial-load spinner -->
        <UIProgressSpinner
          v-if="isLoadingAny && !isFetchingNextPage && !formattedProperties.length"
          class="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"
          width="40"
          height="40"
        />

        <!-- Skeleton on next-page fetch -->
        <div
          v-if="isFetchingNextPage"
          class="grid grid-cols-1 gap-3 pb-4"
        >
          <div
            v-for="n in 3"
            :key="n"
            class="h-[180px] animate-pulse rounded-2xl bg-black-05"
          />
        </div>

        <!-- Load-more error -->
        <div
          v-if="loadMoreFailed"
          class="flex items-center justify-center gap-1.5 py-4 text-[13px] text-black-60"
        >
          <span>{{ t('explore.page.loadMore.failed') }}</span>
          <button
            type="button"
            class="cursor-pointer font-medium text-gold-primary-120 underline"
            @click="retryLoadMore"
          >
            {{ t('explore.page.loadMore.tryAgain') }}
          </button>
        </div>

        <!-- End of list -->
        <div
          v-if="!hasNextPage && formattedProperties.length > 0 && !isLoadingAny && infinitePropertiesEnabled"
          class="py-6 text-center text-[12px] text-black-40"
        >
          {{ t('explore.page.loadMore.allLoaded', { count: propertiesTotal }, propertiesTotal) }}
        </div>

        <!-- Sentinel -->
        <div
          v-if="hasNextPage"
          ref="scrollSentinel"
          style="height: 1px"
        />
      </div>

      <!-- Right column: map (desktop) -->
      <aside
        v-show="isDesktop && !isMapCollapsed"
        :class="[
          'desktop:min-w-0',
          isMapExpanded
            ? 'fixed start-0 end-0 bottom-0 z-[55] w-screen overflow-hidden'
            : 'relative px-0 pb-12 pt-6 desktop:w-[460px] desktop:shrink-0 desktop:px-5 desktop:ps-0 desktop:pe-5 xl:w-[600px] 2xl:w-[760px] 3xl:w-[940px]',
        ]"
        :style="isMapExpanded ? { top: '158px', height: 'calc(100vh - 158px)', maxHeight: 'calc(100vh - 158px)' } : {}"
      >
        <div
          :class="[
            'relative h-full w-full',
            isMapExpanded ? '' : 'desktop:sticky desktop:top-[100px] desktop:h-[calc(100dvh-200px)]',
          ]"
        >
          <ExploreMapColumn
            ref="mapElement"
            :filters="filters"
            :properties="mapProperties"
            :loading="isMapPropertiesLoading || isMapPropertiesFetching"
            :is-expanded="isMapExpanded"
            :highlighted-id="hoveredComplexId"
            card-link-mode
            :selected-id="isMapExpanded ? selectedComplexId : null"
            :zoom="11"
            @update-position="getPropertiesByPosition"
            @update-filters="
              filters = $event;
              applyFilters();
            "
            @toggle-expand="isMapExpanded = !isMapExpanded"
            @select-property="onMarkerSelect"
            @select-position="onSelectPosition"
          />

          <!-- Fullscreen mode: the left list is hidden, so the map-selected
               project's card floats anchored just above its pin (tracks the
               pin on pan/zoom via select-position). Smaller than a list card. -->
          <div
            v-if="isMapExpanded && selectedCard && selectedPinPos"
            class="pointer-events-none absolute z-[60] w-[250px] max-w-[calc(100vw-32px)] -translate-x-1/2 -translate-y-full"
            :style="{ left: selectedPinPos.x + 'px', top: selectedPinPos.y - 14 + 'px' }"
          >
            <div class="pointer-events-auto">
              <div class="mb-1.5 flex items-center justify-end">
                <button
                  type="button"
                  class="inline-flex h-6 items-center gap-1 rounded-full bg-white/95 px-2 text-[10px] font-semibold text-black-70 shadow-sm hover:bg-white"
                  @click="clearMarkerSelection"
                >
                  <svg
                    width="11"
                    height="11"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2.2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    aria-hidden="true"
                  >
                    <path d="M18 6 6 18M6 6l12 12" />
                  </svg>
                  {{ t('common.actions.close') }}
                </button>
              </div>
              <PropertiesProjectCard
                :property="selectedCard"
                :is-favorite="isFavorite(selectedCard.complex_id)"
                is-highlighted
                open-source="map"
                :hero-override="heroFor(selectedCard)?.url ?? null"
                :hero-portrait="heroFor(selectedCard)?.portrait ?? false"
                @toggle-favorite="toggleFavorite(selectedCard.complex_id)"
              />
            </div>
          </div>
        </div>
      </aside>

      <!-- Mobile map drawer -->
      <UIBaseTransition>
        <div
          v-if="isMapOpen && !isDesktop"
          class="relative h-[calc(100dvh-310px)] w-full px-4 pb-6"
        >
          <ExploreMapColumn
            ref="mapElement"
            :filters="filters"
            :properties="mapProperties"
            :loading="isMapPropertiesLoading || isMapPropertiesFetching"
            :zoom="11"
            @update-position="getPropertiesByPosition"
            @update-filters="
              filters = $event;
              applyFilters();
            "
          />
        </div>
      </UIBaseTransition>

      <!-- Scroll-up button -->
      <UIBaseTransition>
        <UIButton
          v-if="showScrollUp"
          id="scroll-up"
          icon
          compact
          class="explore-button !fixed bottom-24 start-8 ms-auto size-10 !p-0"
          @click="scrollToTop"
        >
          <UIIcon name="arrow-big-up" />
        </UIButton>
      </UIBaseTransition>

      <!-- Mobile map/list toggle pill. Previously fixed at top:185px right:16px,
           which landed directly on top of the right-column filter chips and
           ate their taps. Pinned to the bottom-center above the BottomTabBar
           so it never overlaps the filter row. -->
      <button
        v-if="!isDesktop"
        class="fixed left-1/2 z-[70] flex -translate-x-1/2 items-center gap-1.5 rounded-full border border-black/[0.08] bg-white px-5 py-3 text-sm font-semibold text-gold-primary-120 shadow-floating"
        style="bottom: calc(96px + env(safe-area-inset-bottom, 0px))"
        type="button"
        @click="isMapOpen = !isMapOpen"
      >
        <span v-if="isMapOpen">⊞ {{ t('explore.page.list.hideMap') }}</span>
        <span v-else>🗺 {{ t('explore.page.list.showMap') }}</span>
      </button>
    </div>

    <!-- Preview tier: browsing the inventory is a Member feature. The explore UI
         stays visible behind this lock — sees everything, accesses nothing. -->
    <AccessGateModalsHost />

    <BillingPaywallOverlay
      v-if="!exploreGate.allowed.value"
      required-tier="MEMBER"
      :required-tier-label="t('billing.plans.member')"
      :title="t('explore.page.paywall.title')"
      :surface="PAYWALL_SURFACE.explore"
      :teaser="t('explore.page.paywall.teaser')"
      :points="paywallPoints"
    />
  </div>
</template>

<script setup lang="ts">
import { PAYWALL_SURFACE } from '~/constants/paywallSurfaces';
import debounce from 'lodash/debounce';
import { useAutoAnimate } from '@formkit/auto-animate/vue';
import type { Filters, Property } from '@/types/properties';
import { useInfiniteProperties, useMapPins, useGetProperty } from '@/api/queries/properties';
import { useFavoriteProperties } from '@/api/queries/properties';
import { useShowcasePropertiesQuery } from '@/api/queries/trainingShowcase';
import { usePCDictionary, PROPERTIES_DICTIONARY_KEYS } from '@/api/queries/dictionaries';
import { sortPropertiesByField } from '@/helpers/properties';
import { useDeclineStatus } from '~/api/queries/useDeclineStatus';
import { POPUPS } from '@/components/dynamic/maps';
import { useQueryClient } from '@tanstack/vue-query';
import AccessGateModalsHost from '@/components/property/access/modals/AccessGateModalsHost.vue';
import { provideAccessGate } from '@/composables/useAccessGate';
import { UAE_VERIFY_ID_ROUTE } from '~/utils/uae-gate-client/uaeGate';
import { exclusiveGateFor, isExclusiveQueryOn, withExclusiveUrlParam } from '~/utils/exclusives/exclusiveExploreState';

definePageMeta({});

const { t } = useI18n();

useSeoMeta({
  title: () => t('explore.page.seo.title'),
  description: () => t('explore.page.seo.description'),
});

// ID-165: the map's script + tiles come from these hosts; open the connections while the page boots.
useHead({
  link: [
    { rel: 'preconnect', href: 'https://maps.googleapis.com' },
    { rel: 'preconnect', href: 'https://maps.gstatic.com' },
  ],
});

const paywallPoints = computed(() => [
  t('explore.page.paywall.pointFilterable'),
  t('explore.page.paywall.pointCompare'),
  t('explore.page.paywall.pointShortlist'),
]);

const route = useRoute();

const { isDesktop } = useAppBreakpoints();

// Browsing the inventory is a Member feature. Dormant until
// AppSettings.tierGatingEnabled — `allowed` stays true while gating is off, so
// explore renders normally today.
const { gate } = useTierGate();
const exploreGate = gate('projectAccess');

const mapElement = ref<any>(null);
const isMapOpen = ref(false);
const isMapExpanded = ref(false);
// Desktop-only: fully hide the map column so the project list reclaims the
// full width (a bigger, denser list). Independent of isMapOpen (mobile
// drawer) and isMapExpanded (map fullscreen).
const isMapCollapsed = ref(false);
const activeProperty = ref<Property | null>(null);

// Hovering a project card highlights its pin on the map.
const hoveredComplexId = ref<string | null>(null);
const onCardHover = (id: string | null) => {
  hoveredComplexId.value = id;
};

// Clicking a map marker pins that project's CARD to the top of the left list
// (highlighted) — never navigates. Uses the already-loaded list object when
// present, otherwise fetches the full project on demand so the card has its
// photo even when it isn't in the currently loaded list pages.
const selectedComplexId = ref<string | null>(null);
const selectedFromList = computed(
  () => formattedProperties.value.find((p) => String(p.complex_id) === selectedComplexId.value) || null,
);
const selectedQuery = useGetProperty(
  computed(() => (selectedComplexId.value && !selectedFromList.value ? selectedComplexId.value : '')),
  computed(() => !!selectedComplexId.value && !selectedFromList.value),
);
const selectedCard = computed<any>(() => selectedFromList.value || selectedQuery.data.value || null);
// List minus the map-selected project (it's rendered as the first card), so
// it never appears twice.
const listPropertiesWithoutSelected = computed(() =>
  selectedComplexId.value
    ? formattedProperties.value.filter((p) => String(p.complex_id) !== selectedComplexId.value)
    : formattedProperties.value,
);
const onMarkerSelect = (complexId: string) => {
  if (!complexId) return;
  selectedComplexId.value = complexId;
  nextTick(() => {
    if (typeof window === 'undefined') return;
    propertiesContainer.value?.scrollTo?.({ top: 0, behavior: 'smooth' });
    if (!isDesktop.value) window.scrollTo?.({ top: 0, behavior: 'smooth' });
  });
};
const clearMarkerSelection = () => {
  selectedComplexId.value = null;
  selectedPinPos.value = null;
};

// Container-pixel position of the selected pin (emitted by the map on every
// pan/zoom) — used to anchor the floating card above the pin in fullscreen.
const selectedPinPos = ref<{ x: number; y: number } | null>(null);
const onSelectPosition = (pos: { x: number; y: number } | null) => {
  selectedPinPos.value = pos;
};

// Shared card-grid column classes — used by the main list grid AND the
// single-cell grid that holds the pinned "Selected on map" card, so the
// pinned card is exactly the same width as a normal card.
const cardGridColsClass = computed(() =>
  isMapCollapsed.value && isDesktop.value
    ? 'grid-cols-1 tablet:grid-cols-2 desktop:grid-cols-4 2xl:grid-cols-5'
    : isMapOpen.value && isDesktop.value && !isMapExpanded.value
      ? 'grid-cols-1 desktop:grid-cols-2'
      : 'grid-cols-1 tablet:grid-cols-2 desktop:grid-cols-4',
);

const uiStore = useUIStore();

const { data: declineStatus, isLoading: isLoadingDeclineStatus } = useDeclineStatus();

watch(
  [declineStatus, isLoadingDeclineStatus],
  ([status, loading]) => {
    const paymentQuery = route.query.payment;
    if (paymentQuery === 'success') return;
    if (
      !loading &&
      status?.isDeclined &&
      status?.monthlyPackage &&
      status?.originalPackage?.billingPeriod?.toLowerCase() === 'yearly'
    ) {
      uiStore.showPopup(POPUPS.PAYMENT_FAILED);
    }
  },
  { immediate: true },
);

onMounted(() => {
  if (isDesktop.value) {
    isMapOpen.value = true;
  } else {
    isMapOpen.value = false;
  }
});

// Default sort = newest first + only projects that have a hero photo.
// "Newest first" (the unfiltered variant) is still in the dropdown for users
// who want to see brand-new projects whose images haven't been uploaded yet.
const generateDefaultFilters = (): Filters => ({
  query: '',
  property_type_ids: [],
  sale_status: [],
  bedrooms: [],
  payment_plan: [],
  handover_year: [],
  budget: [],
  developer_names: [],
  apartment_type: [],
  order_by: 'created_at',
  order_way: 'DESC',
  exclusive: false,
  favorites_only: false,
  developer_vault_only: false,
  available_only: false,
  with_image_only: true,
});

const filters = await useCreateFilters<Filters>({
  ...generateDefaultFilters(),
});

filters.value.favorites_only = false;

if (!route.query.query && !route.query.search) {
  filters.value.query = '';
}
delete filters.value.ignoreCoordinates;

// Force the default sort on first land if a previously persisted store left it blank.
if (!filters.value.order_by) {
  filters.value.order_by = 'created_at';
  filters.value.order_way = 'DESC';
}

// ── List-head sort dropdown ──────────────────────────────────────
// The `-created_at-with-image` synthetic value bundles two concerns:
//   (1) order by created_at DESC
//   (2) restrict to projects with at least one still hero image
// All the other options leave `with_image_only` off so they keep behaving
// like before (Newest first still shows brand-new image-less projects).
type SortValue = '-created_at-with-image' | '-created_at' | '-updated_at' | '-price' | 'price';

// `key` is the i18n keypath (not pre-resolved text) so both the dropdown list
// and the collapsed button can render it through <i18n-t> — the two price
// options embed a `{arrow}` placeholder (P6b: the → is a sort-direction
// connector, not a trailing CTA glyph, so it stays inside the one message
// but is rendered via a dedicated RTL-mirrored slot instead of a baked-in
// literal character).
const sortOptions = computed<{ value: SortValue; key: string }[]>(() => [
  { value: '-created_at-with-image', key: 'explore.page.sort.default' },
  { value: '-created_at', key: 'explore.page.sort.newestFirst' },
  { value: '-updated_at', key: 'explore.page.sort.recentlyUpdated' },
  { value: 'price', key: 'explore.page.sort.priceLowToHigh' },
  { value: '-price', key: 'explore.page.sort.priceHighToLow' },
]);

const isSortOpen = ref(false);
const sortDropdownRef = ref<HTMLElement | null>(null);

const currentSortValue = computed<SortValue>(() => {
  const orderBy = filters.value.order_by;
  if (!orderBy) return '-created_at-with-image';
  const prefix = filters.value.order_way?.toLowerCase() === 'desc' ? '-' : '';
  // Disambiguate the two created_at-desc options by whether image filter is on.
  if (orderBy === 'created_at' && prefix === '-') {
    return filters.value.with_image_only ? '-created_at-with-image' : '-created_at';
  }
  return `${prefix}${orderBy}` as SortValue;
});

const currentSortKey = computed(
  () => sortOptions.value.find((o) => o.value === currentSortValue.value)?.key ?? sortOptions.value[0].key,
);

const pickSort = (value: SortValue) => {
  if (value === '-created_at-with-image') {
    filters.value.order_by = 'created_at';
    filters.value.order_way = 'DESC';
    filters.value.with_image_only = true;
  } else {
    const hasPrefix = value.startsWith('-');
    filters.value.order_by = hasPrefix ? value.substring(1) : value;
    filters.value.order_way = hasPrefix ? 'DESC' : 'ASC';
    filters.value.with_image_only = false;
  }
  isSortOpen.value = false;
  applyFilters();
};

onMounted(() => {
  if (typeof document === 'undefined') return;
  const handler = (e: MouseEvent) => {
    if (!isSortOpen.value) return;
    const el = sortDropdownRef.value;
    if (el && !el.contains(e.target as Node)) isSortOpen.value = false;
  };
  document.addEventListener('mousedown', handler);
  onBeforeUnmount(() => document.removeEventListener('mousedown', handler));
});

const exploreQuery = ref('');

const paginationData = ref(useCreatePagination());

const isInitialMapLoad = ref(true);

const { showSnackBanner } = useUIStore();

const {
  params: rawParams,
  queryKey,
  applyParams: originalApplyParams,
} = useParseFilters(filters, paginationData, false, withExclusiveUrlParam);

const params = computed(() => {
  const filtered: any = { ...rawParams.value };
  if (filters.value.exclusive) {
    filtered.exclusive = true;
  } else {
    delete filtered.exclusive;
  }
  // available_only is a SERVER filter (getProjectList EXISTS offplan_listings).
  // Forward it only when on, so the URL/query stays clean when off.
  if (filters.value.available_only) {
    filtered.available_only = true;
  } else {
    delete filtered.available_only;
  }
  delete filtered.favorites_only;
  delete filtered.developer_vault_only;
  return filtered;
});

const infinitePropertiesEnabled = computed(() => !filters.value.favorites_only && !filters.value.developer_vault_only);

const mapQueryKey = ref<typeof params.value | null>(null);
mapQueryKey.value = JSON.parse(JSON.stringify(params.value));

watch(
  () => filters.value.query,
  () => {
    mapQueryKey.value = JSON.parse(JSON.stringify(params.value));
  },
);

const applyParams = () => {
  originalApplyParams();
};

const handleAPIError = (_error: any) => {
  // 401 / 403 emirates_id_required on the 100% Commission filter render an inline state instead.
  if (filters.value.exclusive && exclusiveGateFor(_error)) return { pages: [{ data: [], total: 0 }] };
  showSnackBanner('error', t('errors.default'));
  return { pages: [{ data: [], total: 0 }] };
};

const {
  data: properties,
  isLoading: isPropertiesLoading,
  hasNextPage: rawHasNextPage,
  isFetchingNextPage,
  fetchNextPage,
  error: propertiesError,
} = useInfiniteProperties({
  query: params,
  queryKey: computed(() => params.value),
  enabled: infinitePropertiesEnabled,
});

const { data: favoriteProperties, isLoading: isFavoritesLoading } = useFavoriteProperties();

const { data: showcasePropertiesPayload, isLoading: isShowcasePropertiesLoading } = useShowcasePropertiesQuery(
  computed(() => !!filters.value.developer_vault_only),
);

const { data: salesStatuses } = usePCDictionary(PROPERTIES_DICTIONARY_KEYS.SALE_STATUSES);

const getSalesStatusDisplay = (status: string | undefined): string => {
  if (!status) return 'Presale (EOI)';
  const normalizedStatus = status.toLowerCase().replace(/[_\s]/g, '_');
  const statusMap: Record<string, string> = {
    presale_eoi: 'Presale (EOI)',
    presale: 'Presale (EOI)',
    ready: 'Ready',
    off_plan: 'Off Plan',
    sold_out: 'Sold Out',
    on_sale: 'On Sale',
    announcement: 'Announcement',
    launch: 'Launch',
    pending: 'Pending',
    'presale_(eoi)': 'Presale (EOI)',
  };
  return statusMap[normalizedStatus] || status;
};

const getPrelaunchStatusIds = (): number[] => {
  if (!salesStatuses.value) return [];
  const prelaunchLabels = ['Presale (EOI)', 'Announcement', 'Launch'];
  const statusIds: number[] = [];
  (salesStatuses.value as any[]).forEach((status: any) => {
    const displayName = getSalesStatusDisplay(status.name || status.code);
    if (prelaunchLabels.includes(displayName)) statusIds.push(status.id);
  });
  return statusIds;
};

const matchesSaleStatusFilter = (property: any): boolean => {
  if (!filters.value.sale_status || filters.value.sale_status.length === 0) return true;
  if (!property.sales_status?.en) return false;
  const propertyStatusDisplay = getSalesStatusDisplay(property.sales_status.en);
  return filters.value.sale_status.some((statusId: number) => {
    const statusLabel = (salesStatuses.value as any[])?.find((s: any) => s.id === statusId);
    if (!statusLabel) return false;
    const filterStatusDisplay = getSalesStatusDisplay(statusLabel.name || statusLabel.code);
    return filterStatusDisplay === propertyStatusDisplay;
  });
};

const sortProperties = (props: Property[]) => {
  const orderBy = filters.value.order_by || 'created_at';
  const orderWay = (filters.value.order_way || 'DESC').toLowerCase();
  return sortPropertiesByField(props, orderBy, orderWay);
};

const isLoadingAny = computed(() => {
  if (filters.value.developer_vault_only) return isShowcasePropertiesLoading.value;
  if (filters.value.favorites_only) return isFavoritesLoading.value;
  return isPropertiesLoading.value;
});

provide('isPropertiesLoading', isLoadingAny);
provide(
  'filtersQuery',
  computed(() => filters.value.query),
);

const hasNextPage = computed(() => {
  if (filters.value.developer_vault_only) return false;
  return rawHasNextPage.value;
});

watchEffect(() => {
  if (propertiesError.value) handleAPIError(propertiesError.value);
});

const { toggleFavorite, isFavorite, isLoading: isCardLoading, initializeFavorites } = useFavoritesManager();

const formattedProperties = computed(() => {
  if (filters.value.developer_vault_only) {
    let list = [...(showcasePropertiesPayload.value?.data || [])];
    if (filters.value.exclusive) list = list.filter((property) => property.isExclusive);
    if (filters.value.favorites_only) list = list.filter((p) => isFavorite(p.complex_id));
    if (filters.value.sale_status?.length) list = list.filter(matchesSaleStatusFilter);
    return sortProperties(list);
  }

  if (filters.value.favorites_only && !filters.value.exclusive) {
    let favProps = favoriteProperties.value || [];
    if (filters.value.sale_status?.length) favProps = favProps.filter(matchesSaleStatusFilter);
    return sortProperties(favProps);
  }

  if (filters.value.exclusive && filters.value.favorites_only) {
    let favProps = favoriteProperties.value || [];
    let exclusiveFavs = favProps.filter((property) => property.isExclusive);
    if (filters.value.sale_status?.length) exclusiveFavs = exclusiveFavs.filter(matchesSaleStatusFilter);
    return sortProperties(exclusiveFavs);
  }

  const allProperties = properties.value?.pages?.map((page) => page.data).flat() || [];
  if (filters.value.exclusive && !filters.value.favorites_only) {
    return allProperties.filter((property) => property.isExclusive);
  }
  return allProperties;
});

const propertiesTotal = computed(() => {
  if (filters.value.developer_vault_only) return formattedProperties.value.length;
  // The 100% Commission filter is server-side (is_exclusive=1), so its total comes from the server.
  if (filters.value.favorites_only) return formattedProperties.value.length;
  const pages = properties.value?.pages;
  return pages && pages.length > 0 ? pages[pages.length - 1]?.total || 0 : 0;
});

watch(
  () => formattedProperties.value,
  async (props) => {
    if (props.length > 0) {
      const propertyIds = props.map((p) => p.complex_id).filter(Boolean);
      if (propertyIds.length > 0) await initializeFavorites(propertyIds);
    }
  },
  { immediate: true },
);

// ── Landscape hero resolution ─────────────────────────────────────────────
// Cards must lead with a HORIZONTAL image — a portrait source cropped to the
// wide hero box looks awful. The server reads real image dimensions and tells
// us the best landscape still per project (see useLandscapeHeroes); we feed
// that into each card via :hero-override / :hero-portrait. Resolved lazily for
// the loaded list + the map-selected card; cached so a project resolves once.
const { heroMap, resolveHeroes } = useLandscapeHeroes();

const heroSourcesOf = (list: Property[]): { id: string; images: string[] }[] =>
  list
    .map((p) => {
      const id = String(p.complex_id ?? p.id ?? '');
      const gallery = (p as unknown as { gallery?: unknown[] }).gallery;
      // Only resolve from the full gallery. NEVER fall back to [photo]: a
      // single-photo source makes the server return that photo (which may be
      // the portrait lead) and poisons the shared cache. Projects without a
      // gallery on this payload (e.g. the developer-vault/showcase path) are
      // dropped here and resolve later from any list item that carries one.
      const images = Array.isArray(gallery) ? gallery.filter((u): u is string => typeof u === 'string') : [];
      return { id, images };
    })
    .filter((s) => s.id && s.images.length > 0);

const heroFor = (p: Property | null | undefined) => (p ? heroMap.value[String(p.complex_id ?? p.id ?? '')] : undefined);

watch(
  () => formattedProperties.value,
  (list) => {
    if (list?.length) resolveHeroes(heroSourcesOf(list));
  },
  { immediate: true },
);

watch(
  () => selectedCard.value,
  (card) => {
    if (card) resolveHeroes(heroSourcesOf([card as Property]));
  },
);

watch(
  () => filters.value.exclusive,
  () => {
    paginationData.value.current_page = 1;
    applyFilters();
  },
);

// Co-broke brokers (ID-133) sell exclusives only: land them on exclusives; the toggle still shows every project.
const { data: coBrokeAccess } = useCoBrokeAccessQuery();
watch(
  () => coBrokeAccess.value?.isCoBroke === true,
  (isCoBroke) => {
    if (isCoBroke) filters.value.exclusive = true;
  },
  { immediate: true },
);

const showScrollUp = ref(false);
const propertiesContainer = ref<HTMLDivElement | null>(null);
const scrollSentinel = ref<HTMLDivElement | null>(null);
const loadMoreFailed = ref(false);
let scrollObserver: IntersectionObserver | null = null;

watchEffect(() => {
  // IntersectionObserver doesn't exist on the server. Skip on SSR — the
  // observer is only useful for the lazy "load more" trigger once the
  // user is interacting with the page in the browser.
  if (!import.meta.client) return;
  scrollObserver?.disconnect();
  scrollObserver = null;
  if (!scrollSentinel.value) return;
  const root = isDesktop.value ? propertiesContainer.value : null;
  scrollObserver = new IntersectionObserver(
    (entries) => {
      if (entries[0].isIntersecting && hasNextPage.value && !isFetchingNextPage.value && !isLoadingAny.value) {
        loadMoreFailed.value = false;
        fetchNextPage();
      }
    },
    { root, rootMargin: '200px' },
  );
  scrollObserver.observe(scrollSentinel.value);
});

watch(propertiesError, (err) => {
  if (err && !isPropertiesLoading.value && !exclusiveGateFor(err)) loadMoreFailed.value = true;
});

const retryLoadMore = () => {
  loadMoreFailed.value = false;
  fetchNextPage();
};

const checkScroll = () => {
  if (isDesktop.value) {
    showScrollUp.value = formattedProperties.value.length > 10 && Number(propertiesContainer.value?.scrollTop) > 0;
  } else {
    showScrollUp.value = formattedProperties.value.length > 10 && window.scrollY > 0;
  }
};

const scrollToTop = () => {
  if (isDesktop.value) {
    propertiesContainer.value?.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
  } else {
    window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
  }
};

watch(isMapExpanded, (expanded) => {
  if (typeof document === 'undefined') return;
  if (expanded && isDesktop.value) {
    document.body.style.overflow = 'hidden';
  } else {
    document.body.style.overflow = '';
  }
});

const disableScrolls = (disable: boolean) => {
  if (typeof document === 'undefined') return;
  const val = disable ? 'hidden' : '';
  document.documentElement.style.overflow = val;
  document.body.style.overflow = val;
  const mainElement = document.querySelector('main') as HTMLElement | null;
  if (mainElement) mainElement.style.overflow = val;
  if (propertiesContainer.value) propertiesContainer.value.style.overflow = val;
};

watch(
  () => isMapOpen.value && !isDesktop.value,
  (shouldDisable) => {
    nextTick(() => disableScrolls(shouldDisable));
  },
  { immediate: true },
);

onMounted(() => {
  applyFilters();
  if (!isDesktop.value) window.addEventListener('scroll', checkScroll);
});

onUnmounted(() => {
  scrollObserver?.disconnect();
  if (typeof document !== 'undefined') {
    document.body.style.overflow = '';
    document.documentElement.style.overflow = '';
  }
  disableScrolls(false);
  if (!isDesktop.value && typeof window !== 'undefined') {
    window.removeEventListener('scroll', checkScroll);
  }
  getPropertiesByPosition.cancel();
});

const [cardList] = useAutoAnimate();

const mapQueryParams = computed(() => {
  const baseParams = { ...params.value };
  return {
    ...baseParams,
    ignoreCoordinates: false,
    exclusive: filters.value.exclusive || undefined,
    zoom: filters.value.zoom || undefined,
  };
});

// All matching projects as lightweight pins (filter-aware, server-side, no
// 100-row cap) so the map shows every project — not just the first page.
// Map.vue viewport-filters + clusters them client-side.
const {
  data: mapPropertiesRaw,
  isLoading: isMapPropertiesLoading,
  isFetching: isMapPropertiesFetching,
  error: mapError,
} = useMapPins({
  query: mapQueryParams,
  queryKey: mapQueryKey,
});

const mapProperties = computed(() => {
  if (filters.value.developer_vault_only) {
    let list = [...(showcasePropertiesPayload.value?.data || [])];
    if (filters.value.exclusive) list = list.filter((property) => property.isExclusive);
    return list;
  }

  if (filters.value.favorites_only) {
    const favProps = favoriteProperties.value || [];
    if (filters.value.exclusive) return favProps.filter((property) => property.isExclusive);
    return favProps;
  }

  // Map pins are the full, filter-aware set from /map-pins — use them
  // directly. (Previously this fell back to the paginated list page when
  // filters/sort were active, which re-capped the map; the pins endpoint
  // already applies the same filters server-side across ALL projects.)
  return mapPropertiesRaw.value || [];
});

watchEffect(() => {
  if (mapError.value) handleAPIError(mapError.value);
});

const getPropertiesByPosition = debounce(
  ({
    topLeftLat,
    topLeftLng,
    bottomRightLat,
    bottomRightLng,
    zoom,
  }: {
    topLeftLat: number;
    topLeftLng: number;
    bottomRightLat: number;
    bottomRightLng: number;
    zoom: number;
  }) => {
    if (isInitialMapLoad.value) {
      isInitialMapLoad.value = false;
      return;
    }

    filters.value.top_left_lat = topLeftLat;
    filters.value.top_left_lon = topLeftLng;
    filters.value.bottom_right_lat = bottomRightLat;
    filters.value.bottom_right_lon = bottomRightLng;
    filters.value.zoom = zoom;

    const newMapQueryKey = JSON.parse(
      JSON.stringify({
        ...params.value,
        top_left_lat: topLeftLat,
        top_left_lon: topLeftLng,
        bottom_right_lat: bottomRightLat,
        bottom_right_lon: bottomRightLng,
        zoom,
        ignoreCoordinates: false,
      }),
    );

    if (JSON.stringify(mapQueryKey.value) !== JSON.stringify(newMapQueryKey)) {
      mapQueryKey.value = newMapQueryKey;
    }
  },
  1000,
  { leading: false },
);

// "Clear other filters" on the empty 100% Commission list: everything back to defaults, chip stays on.
const clearOtherFilters = () => {
  filters.value = {
    ...generateDefaultFilters(),
    exclusive: true,
    top_left_lat: filters.value.top_left_lat,
    top_left_lon: filters.value.top_left_lon,
    bottom_right_lat: filters.value.bottom_right_lat,
    bottom_right_lon: filters.value.bottom_right_lon,
  };
  delete filters.value.ignoreCoordinates;
  applyFilters();
  mapElement.value?.resetButtons?.();
};

const resetFilters = () => {
  filters.value = {
    ...generateDefaultFilters(),
    top_left_lat: filters.value.top_left_lat,
    top_left_lon: filters.value.top_left_lon,
    bottom_right_lat: filters.value.bottom_right_lat,
    bottom_right_lon: filters.value.bottom_right_lon,
  };
  delete filters.value.ignoreCoordinates;
  applyFilters();
  mapElement.value?.resetButtons?.();
};

const applyFilters = (page?: number) => {
  paginationData.value.current_page = page || 1;
  exploreQuery.value = filters.value.query;

  nextTick(() => {
    applyParams();
    nextTick(() => {
      const newQueryKey = JSON.parse(JSON.stringify(params.value));
      queryKey.value = newQueryKey;
      mapQueryKey.value = { ...newQueryKey };
    });
  });
};

watch(
  () => [salesStatuses.value, route.query.prelaunch] as const,
  ([statuses, prelaunch]) => {
    if (prelaunch !== '1' && prelaunch !== 'true') return;
    if (!statuses?.length) return;
    const ids = getPrelaunchStatusIds();
    if (ids.length > 0) {
      filters.value.sale_status = [...ids];
      nextTick(() => applyFilters(1));
    }
  },
  { immediate: true },
);

const handleSmartSearchSuggestion = (payload: {
  type: 'project' | 'developer' | 'area';
  id: string;
  name: string;
  raw?: unknown;
}) => {
  if (payload.type === 'project') {
    // Prefer SEO slug URL when the autocomplete row carries a developer
    // name. The autocomplete returns `developer` as a string; combined with
    // the project title we can derive `/projects/<dev>/<slug>` without
    // round-tripping the server. Falls back to /properties/<id> when
    // either piece is missing — that path is also covered by the
    // property-id-to-slug-redirect middleware.
    const raw = (payload.raw ?? {}) as { developer?: string | null; title?: string | null };
    const devName = typeof raw.developer === 'string' ? raw.developer : '';
    const projectTitle = typeof raw.title === 'string' ? raw.title : payload.name;
    const devSlug = devName ? normalizeDeveloperName(devName) : '';
    // `projectTitle` may carry the bot's `<DEV> "<TITLE>"` wrapper — strip
    // before slugifying so the URL matches what the server emits.
    const projSlug = projectTitle ? slugify(extractProjectTitle(projectTitle)) : '';
    if (devSlug && projSlug) {
      navigateTo(`/projects/${devSlug}/${projSlug}`);
      return;
    }
    navigateTo(`/properties/${payload.id}`);
    return;
  }
  if (payload.type === 'developer') {
    filters.value.developer_names = [payload.name];
    filters.value.location_ids = [];
    filters.value.query = '';
    filters.value.ignoreCoordinates = true;
    applyFilters(1);
    return;
  }
  if (payload.type === 'area') {
    // Real areas: payload.id is the district name. Curated "virtual" areas
    // (e.g. Dubai Water Canal) expand to several real districts carried on raw,
    // so selecting the chip filters by all of them at once.
    const rawArea = (payload.raw ?? {}) as { virtual?: boolean; districts?: string[] };
    filters.value.location_ids = rawArea.virtual && rawArea.districts?.length ? rawArea.districts : [payload.id];
    filters.value.developer_names = [];
    filters.value.query = '';
    filters.value.ignoreCoordinates = true;
    applyFilters(1);
  }
};

useExploreSearch(filters, exploreQuery, applyFilters);

// ── 100% Commission (ID-118) ─────────────────────────────────────
const authStore = useAuthStore();
const { gate: accessGate } = provideAccessGate();
const { buildRedirectUrl } = useRedirectAfterAuth();
const { sendEventToBackend } = useTracking();
const { catchErrors } = useErrorHandler();
const queryClient = useQueryClient();

const exclusiveError = computed(() => (filters.value.exclusive ? propertiesError.value || mapError.value : null));
const exclusiveGate = computed(() => exclusiveGateFor(exclusiveError.value));
const exclusiveSignInHref = computed(() => buildRedirectUrl('/auth/login'));

// Signed out → the existing guest sign-in prompt; an expired session → the app's standard 401 handling.
watch(exclusiveGate, (next) => {
  if (next !== 'sign_in') return;
  if (authStore.token) catchErrors(exclusiveError.value);
  else accessGate('generic');
});

// Back/forward between /explore and /explore?exclusive=1 keeps the chip in step with the URL.
watch(
  () => route.query.exclusive,
  (raw) => {
    const on = isExclusiveQueryOn(raw);
    if (on !== !!filters.value.exclusive) filters.value.exclusive = on;
  },
);

onMounted(() => {
  if (isExclusiveQueryOn(route.query.exclusive)) {
    void sendEventToBackend('exclusive_filter_toggled', { on: true, source: 'url' });
  }
});

// Verifying the phone from a card's commission gate unlocks AED amounts: refetch the list and pins.
watch(
  () => authStore.user?.phoneVerified,
  (verified, wasVerified) => {
    if (verified && !wasVerified) queryClient.invalidateQueries({ queryKey: ['properties'] });
  },
);

// Hover sync between list and map
const onListItemHover = (property: Property | null) => {
  activeProperty.value = property;
};
</script>

<style scoped>
.explore-button,
:deep(.explore-button.button) {
  box-shadow:
    0px 2px 4px 0px #0f151d33,
    0px 1px 10px 0px #0000001f,
    0px 4px 5px 0px #00000024;
}
</style>
